/**
 * Flow map UI copy: the one bilingual table every part of <FlowMap> reads its
 * words from (toolbar, inspector, table, the static picture's empty state).
 *
 * Components never inline a user-visible word. They receive a `FlowMapLabels`
 * object from `flowMapLabels(locale, overrides)`, so a host with its own
 * catalogue (the CRM) can replace any string without forking a component.
 *
 * Estonian is chosen for any locale starting with "et"; everything else gets
 * English. There is deliberately no fallback to Estonian for an unknown or
 * missing locale: English is the neutral choice for a visitor we cannot place.
 *
 * React-free and dependency-free: the static render, the live editor and the
 * tests all import it.
 */
import type { FlowEdgeKind, FlowMapLabels, FlowNodeKind, FlowTone } from "./types.js";

/** The keys of `T` whose value is a plain string (the function-valued labels are excluded). */
export type StringKeyOf<T> = { [K in keyof T]: T[K] extends string ? K : never }[keyof T];

/** A label key that maps to a plain string. */
export type FlowMapStringKey = StringKeyOf<FlowMapLabels>;

/** The label key naming each node kind (badges, chips, the add menu, the kind select). */
export const FLOW_KIND_LABEL: Readonly<Record<FlowNodeKind, FlowMapStringKey>> = {
  start: "kindStart",
  step: "kindStep",
  decision: "kindDecision",
  end: "kindEnd",
  note: "kindNote",
  group: "kindGroup",
};

/** The label key naming each tone (the inspector's colour chips). */
export const FLOW_TONE_LABEL: Readonly<Record<FlowTone, FlowMapStringKey>> = {
  neutral: "toneNeutral",
  primary: "tonePrimary",
  success: "toneSuccess",
  warning: "toneWarning",
  danger: "toneDanger",
  info: "toneInfo",
};

/** The label key naming each arrow kind (the edge kind select). */
export const FLOW_EDGE_KIND_LABEL: Readonly<Record<FlowEdgeKind, FlowMapStringKey>> = {
  default: "edgeDefault",
  yes: "edgeYes",
  no: "edgeNo",
  optional: "edgeOptional",
};

export const FLOWMAP_UI: Record<FlowMapStringKey, { et: string; en: string }> = {
  view: { et: "Vaade", en: "View" },
  canvasView: { et: "Lõuend", en: "Canvas" },
  tableView: { et: "Tabel", en: "Table" },
  add: { et: "Lisa", en: "Add" },
  autoLayout: { et: "Korrasta", en: "Arrange" },
  directionTB: { et: "Ülevalt alla", en: "Top to bottom" },
  directionLR: { et: "Vasakult paremale", en: "Left to right" },
  fitView: { et: "Mahuta vaatesse", en: "Fit to view" },
  zoomIn: { et: "Suurenda", en: "Zoom in" },
  zoomOut: { et: "Vähenda", en: "Zoom out" },
  undo: { et: "Võta tagasi", en: "Undo" },
  redo: { et: "Tee uuesti", en: "Redo" },
  deleteSelected: { et: "Kustuta valitud", en: "Delete selected" },
  kindStart: { et: "Algus", en: "Start" },
  kindStep: { et: "Samm", en: "Step" },
  kindDecision: { et: "Otsus", en: "Decision" },
  kindEnd: { et: "Lõpp", en: "End" },
  kindNote: { et: "Märkus", en: "Note" },
  kindGroup: { et: "Rühm", en: "Group" },
  toneNeutral: { et: "Neutraalne", en: "Neutral" },
  tonePrimary: { et: "Põhitoon", en: "Primary" },
  toneSuccess: { et: "Roheline", en: "Green" },
  toneWarning: { et: "Kollane", en: "Yellow" },
  toneDanger: { et: "Punane", en: "Red" },
  toneInfo: { et: "Sinine", en: "Blue" },
  edgeDefault: { et: "Tavaline", en: "Plain" },
  edgeYes: { et: "Jah", en: "Yes" },
  edgeNo: { et: "Ei", en: "No" },
  edgeOptional: { et: "Valikuline", en: "Optional" },
  a11yNode: {
    et: "Vajuta Enter või tühikut, et samm valida. Kustutamiseks vajuta Delete, loobumiseks Escape.",
    en: "Press enter or space to select a step. Press delete to remove it and escape to cancel.",
  },
  a11yNodeMove: {
    et: "Vajuta Enter või tühikut, et samm valida. Seejärel liiguta seda nooleklahvidega. Kustutamiseks vajuta Delete, loobumiseks Escape.",
    en: "Press enter or space to select a step. You can then use the arrow keys to move it. Press delete to remove it and escape to cancel.",
  },
  a11yNodeMoved: { et: "Samm liigutatud. Uus asukoht: x {x}, y {y}.", en: "Step moved. New position, x: {x}, y: {y}." },
  a11yEdge: {
    et: "Vajuta Enter või tühikut, et ühendus valida. Seejärel kustutamiseks Delete, loobumiseks Escape.",
    en: "Press enter or space to select a connection. You can then press delete to remove it or escape to cancel.",
  },
  a11yMinimap: { et: "Kaardi pisipilt", en: "Map overview" },
  a11yHandle: { et: "Ühenduse punkt", en: "Connection point" },
  inspector: { et: "Üksikasjad", en: "Details" },
  title: { et: "Pealkiri", en: "Title" },
  description: { et: "Kirjeldus", en: "Description" },
  kind: { et: "Tüüp", en: "Type" },
  tone: { et: "Toon", en: "Color" },
  steps: { et: "Alamsammud", en: "Sub-steps" },
  addStep: { et: "Lisa alamsamm", en: "Add sub-step" },
  stepPlaceholder: { et: "Uus alamsamm", en: "New sub-step" },
  links: { et: "Lingid", en: "Links" },
  addLink: { et: "Lisa link", en: "Add link" },
  linkLabel: { et: "Lingi tekst", en: "Link text" },
  linkHref: { et: "Aadress", en: "Address" },
  linkNode: { et: "Samm kaardil", en: "Step on the map" },
  linkInvalid: {
    et: "Aadress peab algama märgiga / või olema https://, mailto: või tel: link",
    en: "The address must start with / or be an https://, mailto: or tel: link",
  },
  noNode: { et: "Puudub", en: "None" },
  parent: { et: "Rühm", en: "Group" },
  noParent: { et: "Rühmata", en: "No group" },
  connections: { et: "Ühendused", en: "Connections" },
  addConnection: { et: "Lisa ühendus", en: "Add connection" },
  pickSearch: { et: "Otsi", en: "Search" },
  pickNoMatch: { et: "Sellist ei leitud", en: "Nothing by that name" },
  target: { et: "Kuhu", en: "To" },
  edgeLabel: { et: "Silt", en: "Label" },
  edgeKind: { et: "Liik", en: "Type" },
  deleteNode: { et: "Kustuta samm", en: "Delete step" },
  deleteEdge: { et: "Kustuta ühendus", en: "Delete connection" },
  remove: { et: "Eemalda", en: "Remove" },
  close: { et: "Sulge", en: "Close" },
  openLink: { et: "Ava link", en: "Open link" },
  goToNode: { et: "Näita kaardil", en: "Show on map" },
  untitled: { et: "Nimetu samm", en: "Untitled step" },
  colOrder: { et: "#", en: "#" },
  colTitle: { et: "Pealkiri", en: "Title" },
  colKind: { et: "Tüüp", en: "Type" },
  colFrom: { et: "Kust", en: "From" },
  colTo: { et: "Kuhu", en: "To" },
  colSteps: { et: "Alamsammud", en: "Sub-steps" },
  colLinks: { et: "Lingid", en: "Links" },
  sortBy: { et: "Sorteeri: {column}", en: "Sort by {column}" },
  emptyTitle: { et: "Kaart on tühi", en: "The map is empty" },
  emptyHint: {
    et: "Lisa esimene samm nupust Lisa või topeltklõpsa lõuendil.",
    en: "Add the first step with the Add button or double-click the canvas.",
  },
  emptyReadOnly: { et: "Siin ei ole veel ühtegi sammu.", en: "There are no steps here yet." },
  canvasHint: {
    et: "Topeltklõps tühjal alal lisab sammu. Ühendamiseks lohista sammu servapunktist teise sammu peale.",
    en: "Double-click an empty spot to add a step. To connect two steps, drag from a dot on one step's edge onto the other.",
  },
  loading: { et: "Laen...", en: "Loading..." },
};

type Lang = "et" | "en";

/** "et", "et-EE", "ET" read Estonian; anything else (including nothing) reads English. */
function langOf(locale: string | undefined): Lang {
  return locale?.toLowerCase().startsWith("et") ? "et" : "en";
}

const COUNTS: Record<Lang, Pick<FlowMapLabels, "nodesCount" | "edgesCount" | "stepsProgress">> = {
  et: {
    nodesCount: (n) => (n === 1 ? "1 samm" : `${n} sammu`),
    edgesCount: (n) => (n === 1 ? "1 ühendus" : `${n} ühendust`),
    stepsProgress: (done, total) => `${done}/${total}`,
  },
  en: {
    nodesCount: (n) => (n === 1 ? "1 step" : `${n} steps`),
    edgesCount: (n) => (n === 1 ? "1 connection" : `${n} connections`),
    stepsProgress: (done, total) => `${done}/${total}`,
  },
};

function buildBase(lang: Lang): FlowMapLabels {
  const out = { ...COUNTS[lang] } as FlowMapLabels;
  const strings = out as unknown as Record<FlowMapStringKey, string>;
  for (const key of Object.keys(FLOWMAP_UI) as FlowMapStringKey[]) strings[key] = FLOWMAP_UI[key][lang];
  return Object.freeze(out);
}

/** One frozen object per language, so a caller without overrides gets a stable identity (cheap React deps). */
const BASE: Record<Lang, FlowMapLabels> = { et: buildBase("et"), en: buildBase("en") };

/**
 * The full label set for `locale`, with `overrides` applied on top. An override
 * is used only when it has the right type (a string for a text key, a function
 * for a count key), so a stray `undefined` or a mistyped host value never blanks
 * a label. Without usable overrides the shared frozen object is returned.
 */
export function flowMapLabels(locale: string | undefined, overrides?: Partial<FlowMapLabels>): FlowMapLabels {
  const base = BASE[langOf(locale)];
  if (!overrides) return base;
  let out: Record<string, unknown> | null = null;
  for (const [key, value] of Object.entries(overrides)) {
    if (!(key in base)) continue;
    const current = (base as unknown as Record<string, unknown>)[key];
    if (typeof value !== typeof current) continue;
    if (typeof value === "string" && value.length === 0) continue;
    out ??= { ...base };
    out[key] = value;
  }
  return out ? (out as unknown as FlowMapLabels) : base;
}
